import React from 'react';
import { sortBy } from 'lodash';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';

import MarkerClustererPlus from '@google/markerclustererplus';
import _ from 'helpers/no_conflict_lodash';

import { mapOptionPropTypes, markerEvents, mapStyles } from 'helpers/map_helpers';

export default class ClusteredMap extends React.Component {
  static propTypes = {
    ...mapOptionPropTypes,
    activeMarkerId: PropTypes.any,
    clustererStyles: PropTypes.arrayOf(PropTypes.shape({
      height: PropTypes.number,
      iconAnchor: PropTypes.array,
      url: PropTypes.string,
      width: PropTypes.number
    })).isRequired,
    disableInfoWindowHeader: PropTypes.bool,
    google: PropTypes.object,
    infoWindow: PropTypes.bool,
    infoWindowContent: PropTypes.element,
    mapContainerRef: PropTypes.object,
    markers: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.any.isRequired,
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
      icon: PropTypes.shape({
        anchor: PropTypes.array,
        url: PropTypes.string.isRequired
      })
    })),
    maxMarkerClustererZoom: PropTypes.number,
    minimumClusterSize: PropTypes.number,
    zoom: PropTypes.number,
    onMapDrag: PropTypes.func,
    onMapIdle: PropTypes.func,
    onMapZoomChanged: PropTypes.func,
    onMarkerMouseover: PropTypes.func.isRequired,
    onReady: PropTypes.func
  };

  static defaultProps = {
    fullscreenControl: true,
    infoWindow: true,
    mapTypeControl: true,
    markers: [],
    minimumClusterSize: 5,
    zoom: 14,
    onMapDrag: () => {},
    onMapIdle: () => {},
    onMapZoomChanged: () => {}
  };

  mapRef = React.createRef();
  renderedMarkers = [];

  componentDidMount() {
    this.initializeMap();
    this.refresh();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.google == null && this.props.google != null) {
      this.initializeMap();
    }

    if (this.map) {
      const mapOptions = Object.keys(mapOptionPropTypes);
      const indexOfChangedParam = _.findIndex(mapOptions, mapOption => (
        prevProps[mapOption] !== this.props[mapOption]
      ));

      if (indexOfChangedParam > -1) {
        const mapParams = _.pick(this.props, mapOptions);
        this.map.setOptions(mapParams);
      }
    }

    this.refresh();
  }

  componentWillUnmount() {
    if (this.markerClusterer) {
      this.markerClusterer.clearMarkers();
      this.markerClusterer.setMap(null);
    }
  }

  buildIcon(iconData) {
    if (iconData == null) {
      return;
    }

    const { anchor, ...data } = iconData;
    return {
      anchor: new google.maps.Point(...anchor),
      ...data
    };
  }

  buildInfoWindow() {
    const { activeMarkerId, google, infoWindow, infoWindowContent } = this.props;
    const markerData = this.renderedMarkers.find(m => m.id === activeMarkerId);

    this.infoWindowContainer = document.createElement('div');
    this.infoWindowContainer.setAttribute("id", "info-window-container");

    if (markerData && markerData.marker && infoWindow) {
      this.infoWindow = {
        id: activeMarkerId,
        windowObject: new google.maps.InfoWindow({
          content: this.infoWindowContainer,
          headerDisabled: this.props.disableInfoWindowHeader,
        })
      };
      this.openInfoWindow(this.infoWindow.windowObject, markerData.marker);
      ReactDOM.render(infoWindowContent, this.infoWindowContainer);
    }
  }

  buildMarkers(markersData) {
    const newMarkers = [];
    const newMarkerData = [];

    _.each(markersData, (markerData) => {
      const position = new google.maps.LatLng(markerData.lat, markerData.lng);
      const marker = new google.maps.Marker({
        icon: this.buildIcon(markerData.icon),
        position: position
      });
      _.each(
        markerEvents,
        event => marker.addListener(event, e => this.handleMarkerEvent(event, markerData.id, e))
      );
      newMarkers.push(marker);
      newMarkerData.push({ ...markerData, marker });
    });

    this.markerClusterer.addMarkers(newMarkers);
    return newMarkerData;
  }

  handleMarkerEvent(eventType, id, event) {
    const eventKey = _.upperFirst(_.camelCase(eventType));
    const handler = this.props[`onMarker${eventKey}`];

    if (handler != null) {
      handler(id, event);
    }
  }

  handleReady() {
    const { onReady } = this.props;

    if (onReady != null) {
      onReady(this.props, this.map);
    }
  }

  initializeMap() {
    const { clustererStyles, google, maxMarkerClustererZoom, minimumClusterSize, zoom } = this.props;
    const { current: mapElement } = this.mapRef;
    const mapParams = _.pick(this.props, Object.keys(mapOptionPropTypes));
    mapParams.zoom = zoom;

    if (google && mapElement) {
      this.map = new google.maps.Map(
        mapElement,
        mapParams
      );

      this.map.addListener('drag', () => this.props.onMapDrag(this.map));
      this.map.addListener('idle', () => this.props.onMapIdle(this.map));
      this.map.addListener('zoom_changed', () => this.props.onMapZoomChanged(this.map));

      this.markerClusterer = new MarkerClustererPlus(this.map, [], {
        minimumClusterSize,
        maxZoom: maxMarkerClustererZoom,
        styles: clustererStyles
      });
      this.handleReady();
    }
  }

  openInfoWindow(windowObject, marker) {
    const { minimumClusterSize } = this.props;
    const cluster = this.markerClusterer.getClusters().find(cluster => cluster.getMarkers().indexOf(marker) > -1);

    if (!cluster) {
      return;
    } else if (cluster.getSize() >= minimumClusterSize) {
      windowObject.setPosition(cluster.getCenter());
      windowObject.open(this.map);
    } else {
      windowObject.open(this.map, marker);
    }
  }

  refresh() {
    this.refreshMarkers();
    this.refreshInfoWindow();
  }

  refreshInfoWindow() {
    if (this.map == null) {
      return;
    }

    const { activeMarkerId } = this.props;

    if (this.infoWindow) {
      this.updateInfoWindow();
    } else if (activeMarkerId != null) {
      this.buildInfoWindow();
    }
  }

  updateInfoWindow() {
    const { activeMarkerId, infoWindow, infoWindowContent } = this.props;
    const windowObject = this.infoWindow.windowObject;

    if (activeMarkerId == null) {
      windowObject.close();
      this.infoWindow = null;
    } else if (this.infoWindow.id !== activeMarkerId) {
      const markerData = this.renderedMarkers.find(m => m.id === activeMarkerId);
      windowObject.close();

      if (markerData && markerData.marker && infoWindow) {
        this.infoWindow.id = activeMarkerId;
        this.openInfoWindow(windowObject, markerData.marker);
      } else {
        this.infoWindow = null;
      }
    }
    windowObject.setContent(ReactDOM.render(infoWindowContent, this.infoWindowContainer));
  }

  refreshMarkers() {
    if (this.map == null) {
      return;
    }

    const { markers } = this.props;
    const markerIds = markers.map(m => m.id);
    const [keepMarkers, deleteMarkers] = _.partition(this.renderedMarkers, m => _.includes(markerIds, m.id));
    const markersToAdd = [];
    this.markerClusterer.removeMarkers(deleteMarkers.map(m => m.marker));

    markers.each((m) => {
      const matchIndex = keepMarkers.findIndex(existing => existing.id === m.id);
      if (matchIndex > -1) {
        let existingMarker = keepMarkers[matchIndex];
        if (existingMarker.lat !== m.lat || existingMarker.lng !== m.lng) {
          const newPosition = new google.maps.LatLng(m.lat, m.lng);
          existingMarker.marker.setPosition(newPosition);
          existingMarker = { marker: existingMarker.marker, ...m };
        }
        if (!_.isEqual(existingMarker.icon, m.icon)) {
          existingMarker.marker.setIcon(this.buildIcon(m.icon));
          existingMarker = { icon: m.icon, ...existingMarker };
        }

        keepMarkers[matchIndex] = existingMarker;
      } else {
        markersToAdd.push(m);
      }
    });

    const markersSortedToAdd = sortBy(markersToAdd, 'id').reverse();
    const newMarkers = this.buildMarkers(markersSortedToAdd);
    this.renderedMarkers = [...keepMarkers, ...newMarkers];
  }

  render() {
    return (
      <div ref={this.props.mapContainerRef} style={mapStyles.container}>
        <div ref={this.mapRef} style={mapStyles.map} />
      </div>
    );
  }
}
